import React, { useEffect, useMemo, useRef, useState } from "react";

import "../css/trivia.css";

import { getApp } from "firebase/app";
import { doc, getDoc, getFirestore } from "firebase/firestore";

// Trivia (public/play)
// Loads categories/questions from Firestore (same doc as editor), renders a clickable wheel.
// On landing: picks a random question from that category and shows "Reveal answer".

function getDbSafe() {
    // Assumes Firebase initializeApp() already ran elsewhere.
    return getFirestore(getApp());
}

function clamp(n, a, b) {
    return Math.max(a, Math.min(b, n));
}

function randInt(maxExclusive) {
    return Math.floor(Math.random() * maxExclusive);
}

function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
}

function hashStringToInt(str) {
    let h = 2166136261;
    for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

function getCategoryColor(category) {
    // Stable, readable palette (no neon)
    const palette = [
        "#1c3650", // SOLE navy
        "#2f4f6f", // slate blue
        "#345c7d", // medium navy
        "#3f6f8f", // blue-gray
        "#c7a34a", // muted gold
        "#d2b55c", // soft gold
        "#9bb7d0", // light blue
        "#b9cfe3", // lighter blue
        "#6b7f93", // steel
        "#8aa2b8", // mist blue
    ];

    const key = (category?.id || category?.name || "").toString();
    const idx = hashStringToInt(key) % palette.length;
    return palette[idx];
}

function withAlpha(hex, a) {
    // hex: #rrggbb -> rgba(r,g,b,a)
    const h = hex.replace("#", "");
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
}

function hexToRgb(hex) {
    const h = (hex || "").replace("#", "");
    if (h.length !== 6) return { r: 0, g: 0, b: 0 };
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
}

function relativeLuminance({ r, g, b }) {
    // WCAG-ish luminance (good enough for deciding light/dark text)
    const srgb = [r, g, b].map((v) => {
        const c = v / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
}

function pickTextColorForHex(hex) {
    const lum = relativeLuminance(hexToRgb(hex));
    // If background is dark, use white text
    return lum < 0.45 ? "rgba(255,255,255,0.92)" : "rgba(0,0,0,0.82)";
}

export default function Trivia() {
    // NOTE: Same path used by the editor.
    const TRIVIA_DOC_PATH = "triviaSets/default";

    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState("");

    const [categories, setCategories] = useState([]); // {id, name}
    const [questions, setQuestions] = useState([]); // {id, categoryId, question, answer}

    const [isSpinning, setIsSpinning] = useState(false);
    const [selectedCategoryId, setSelectedCategoryId] = useState("");
    const [activeQuestionId, setActiveQuestionId] = useState("");
    const [isAnswerRevealed, setIsAnswerRevealed] = useState(false);

    // Session tracking of used question IDs (prevents repeats until exhausted)
    const usedRef = useRef(new Set());

    // Wheel
    const canvasRef = useRef(null);
    const rotationRef = useRef(0); // radians
    const animRef = useRef(null);

    const categoryById = useMemo(() => {
        const m = new Map();
        for (const c of categories) m.set(c.id, c);
        return m;
    }, [categories]);

    const questionsByCategory = useMemo(() => {
        const m = new Map();
        for (const c of categories) m.set(c.id, []);
        for (const q of questions) {
            if (!m.has(q.categoryId)) m.set(q.categoryId, []);
            m.get(q.categoryId).push(q);
        }
        return m;
    }, [categories, questions]);

    const activeCategory = selectedCategoryId ? categoryById.get(selectedCategoryId) : null;
    const activeQuestion = activeQuestionId ? questions.find((q) => q.id === activeQuestionId) : null;

    async function loadTrivia() {
        setIsLoading(true);
        setLoadError("");

        try {
            const db = getDbSafe();
            const ref = doc(db, TRIVIA_DOC_PATH);
            const snap = await getDoc(ref);

            if (!snap.exists()) {
                setCategories([]);
                setQuestions([]);
                setLoadError("No trivia set found yet. Build one in the editor first.");
                return;
            }

            const data = snap.data() || {};
            const loadedCategories = Array.isArray(data.categories) ? data.categories : [];
            const loadedQuestions = Array.isArray(data.questions) ? data.questions : [];

            setCategories(loadedCategories);
            setQuestions(loadedQuestions);

            // reset session state
            usedRef.current = new Set();
            setSelectedCategoryId("");
            setActiveQuestionId("");
            setIsAnswerRevealed(false);

            // reset wheel
            rotationRef.current = 0;
            redraw();
        } catch (err) {
            console.error(err);
            setLoadError("Could not load trivia from Firebase.");
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        loadTrivia();
        // eslint-disable-next-line react-hooks/exhaustive-deps
        return () => {
            if (animRef.current) cancelAnimationFrame(animRef.current);
        };
    }, []);

    // --- Wheel drawing ---

    function drawWheel(ctx, w, h) {
        ctx.clearRect(0, 0, w, h);

        const cx = w / 2;
        const cy = h / 2;
        const radius = Math.min(w, h) * 0.39;

        if (!categories.length) {
            ctx.fillStyle = "rgba(0,0,0,0.55)";
            ctx.font = "600 14px system-ui, -apple-system, Segoe UI, Roboto, Arial";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(isLoading ? "Loading…" : "No categories", cx, cy);
            return;
        }

        const n = categories.length;
        const slice = (Math.PI * 2) / n;
        const rot = rotationRef.current;

        for (let i = 0; i < n; i++) {
            const start = rot + i * slice;
            const end = start + slice;

            const even = i % 2 === 0;
            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.arc(cx, cy, radius, start, end);
            ctx.closePath();
            const base = getCategoryColor(categories[i]);

            // stronger colors
            ctx.fillStyle = even ? base : withAlpha(base, 0.8);
            ctx.fill();

            ctx.strokeStyle = "rgba(0,0,0,0.08)";
            ctx.lineWidth = 1;
            ctx.stroke();

            // label
            const mid = (start + end) / 2;
            const tx = cx + Math.cos(mid) * (radius * 0.62);
            const ty = cy + Math.sin(mid) * (radius * 0.62);

            ctx.save();
            ctx.translate(tx, ty);
            ctx.rotate(mid);

            // Choose label color based on slice color for readability
            const labelColor = pickTextColorForHex(base);
            ctx.fillStyle = labelColor;
            // subtle outline to stay readable on mid-tone colors
            ctx.lineWidth = 3;
            ctx.strokeStyle = labelColor.includes("255") ? "rgba(0,0,0,0.30)" : "rgba(255,255,255,0.35)";
            // Larger label text for crowd readability; scales down if many categories
            const labelPx = clamp(28 - n * 3, 18, 22);
            ctx.font = `800 ${labelPx}px system-ui, -apple-system, Segoe UI, Roboto, Arial`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";

            const name = categories[i].name || "";
            const maxLen = 18;
            const label = name.length > maxLen ? name.slice(0, maxLen - 1) + "…" : name;
            ctx.strokeText(label, 0, 0);
            ctx.fillText(label, 0, 0);

            ctx.restore();
        }

        // center cap
        ctx.beginPath();
        ctx.arc(cx, cy, radius * 0.16, 0, Math.PI * 2);
        ctx.fillStyle = "#1c3650";
        ctx.fill();

        ctx.fillStyle = "#fff";
        ctx.font = "800 20px system-ui, -apple-system, Segoe UI, Roboto, Arial";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(isSpinning ? "Spinning" : "Spin", cx, cy);

        // pointer at top (flipped to point down toward the wheel)
        const px = cx;
        const py = cy - radius - 12;
        ctx.beginPath();
        ctx.moveTo(px - 12, py);
        ctx.lineTo(px + 12, py);
        ctx.lineTo(px, py + 18);
        ctx.closePath();
        ctx.fillStyle = "#1c3650";
        ctx.fill();
    }

    function redraw() {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const parent = canvas.parentElement;
        const size = clamp(parent?.clientWidth || 720, 420, 980);

        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.floor(size * dpr);
        canvas.height = Math.floor(size * dpr);
        canvas.style.width = `${size}px`;
        canvas.style.height = `${size}px`;

        const ctx = canvas.getContext("2d");
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        drawWheel(ctx, size, size);
    }

    useEffect(() => {
        redraw();
        const onResize = () => redraw();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categories, isSpinning, isLoading]);

    function pickQuestionForCategory(categoryId) {
        const pool = questionsByCategory.get(categoryId) || [];
        if (!pool.length) return null;

        // Prefer unused questions this session
        let available = pool.filter((q) => !usedRef.current.has(q.id));

        // If exhausted, reset for this category so we can keep going
        if (!available.length) {
            for (const q of pool) usedRef.current.delete(q.id);
            available = pool.slice();
        }

        const picked = available[randInt(available.length)];
        usedRef.current.add(picked.id);
        return picked;
    }

    function computeCategoryFromRotation() {
        const n = categories.length;
        if (!n) return null;

        const slice = (Math.PI * 2) / n;

        // Normalize rotation to [0, 2pi)
        let r = rotationRef.current % (Math.PI * 2);
        if (r < 0) r += Math.PI * 2;

        // pointer at top (270deg)
        const pointerAngle = (Math.PI * 3) / 2;
        const a = (pointerAngle - r + Math.PI * 2) % (Math.PI * 2);
        const idx = Math.floor(a / slice) % n;

        return categories[idx] || null;
    }

    function spin() {
        if (isSpinning || !categories.length) return;

        setIsSpinning(true);
        setIsAnswerRevealed(false);

        const start = rotationRef.current;
        const spins = 4 + Math.random() * 3; // 4-7 turns
        const extra = Math.random() * Math.PI * 2;
        const target = start + spins * Math.PI * 2 + extra;

        const durationMs = 2200 + Math.random() * 900;
        const t0 = performance.now();

        if (animRef.current) cancelAnimationFrame(animRef.current);

        const step = (now) => {
            const t = clamp((now - t0) / durationMs, 0, 1);
            const e = easeOutCubic(t);
            rotationRef.current = start + (target - start) * e;
            redraw();

            if (t < 1) {
                animRef.current = requestAnimationFrame(step);
            } else {
                setIsSpinning(false);
                const cat = computeCategoryFromRotation();
                if (cat) {
                    setSelectedCategoryId(cat.id);
                    const q = pickQuestionForCategory(cat.id);
                    setActiveQuestionId(q ? q.id : "");
                } else {
                    setSelectedCategoryId("");
                    setActiveQuestionId("");
                }
            }
        };

        animRef.current = requestAnimationFrame(step);
    }

    return (
        <div className="triviaPlay">
            <div className="triviaPlayHeader">
                <div>
                    <h1 className="triviaPlayTitle">Trivia</h1>
                    <div className="triviaPlaySubtitle">
                        Click the wheel to spin. When it lands, answer the question, then reveal.
                    </div>
                </div>

                <div className="triviaPlayActions">
                    <button className="triviaBtn" type="button" onClick={loadTrivia} disabled={isLoading || isSpinning}>
                        Reload
                    </button>
                </div>
            </div>

            {loadError ? <div className="triviaPlayAlert">{loadError}</div> : null}

            <div className="triviaPlayGrid">
                <div className="triviaWheelCard">
                    <div className="triviaWheelWrap">
                        <canvas
                            ref={canvasRef}
                            className={isSpinning ? "triviaWheelCanvas triviaWheelCanvasSpinning" : "triviaWheelCanvas"}
                            onClick={spin}
                            role="button"
                            tabIndex={0}
                            aria-label="Spin the trivia wheel"
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    spin();
                                }
                            }}
                        />
                    </div>

                    <div className="triviaWheelHint">
                        {isLoading
                            ? "Loading…"
                            : !categories.length
                                ? "Add categories in the editor first."
                                : isSpinning
                                    ? "Spinning…"
                                    : "Click to spin"}
                    </div>
                </div>

                <div className="triviaQACard">
                    <div className="triviaQARow">
                        <div>
                            <div className="triviaQALabel">Category</div>
                            <div className="triviaQAValue">{activeCategory ? activeCategory.name : "—"}</div>
                        </div>
                    </div>

                    <div className="triviaQABlock">
                        <div className="triviaQALabel">Question</div>
                        <div className="triviaQAQuestion">
                            {activeQuestion ? activeQuestion.question : "Spin the wheel to get a question."}
                        </div>
                    </div>

                    <div className="triviaQABlock">
                        <div className="triviaQALabel">Answer</div>
                        <div className={isAnswerRevealed ? "triviaQAAnswer" : "triviaQAAnswer triviaQAAnswerHidden"}>
                            {activeQuestion ? activeQuestion.answer : "—"}
                        </div>
                    </div>

                    <div className="triviaQAActions">
                        <button
                            className="triviaBtn"
                            type="button"
                            onClick={() => setIsAnswerRevealed((v) => !v)}
                            disabled={!activeQuestion}
                        >
                            {isAnswerRevealed ? "Hide answer" : "Reveal answer"}
                        </button>

                        <button
                            className="triviaBtn"
                            type="button"
                            onClick={() => {
                                if (!selectedCategoryId) return;
                                setIsAnswerRevealed(false);
                                const q = pickQuestionForCategory(selectedCategoryId);
                                setActiveQuestionId(q ? q.id : "");
                            }}
                            disabled={!selectedCategoryId || isSpinning}
                        >
                            Next question
                        </button>

                        <button className="triviaBtn" type="button" onClick={spin} disabled={isSpinning || !categories.length}>
                            Spin again
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
